// Ranking del mes por hotel + ficha en bottom-sheet.
import React, { useEffect, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View , RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Header from '@/components/Header';
import RankingCards from '@/components/RankingCards';
import PuntosVenta from '@/components/PuntosVenta';
import { Cargando } from '@/components/MarcaOna';
import { Comercial } from '@/components/modules';
import RoleSheet from '@/components/RoleSheet';
import { BottomSheet, Card, KpiTile, PmsBadge, Spark } from '@/components/ui';
import { api, type HotelRow, type Source } from '@/api/client';
import { refrescarTodo, suscribir, valorActual } from '@/lib/refresco';
import { C, F, R, heatColor } from '@/constants/theme';
import { useRole } from '@/context/RoleContext';
import { fmt, fmtDec, fmtE, pct1 } from '@/lib/format';
import { Chip } from '@/components/ui';

const ddmm = (iso?: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');

/* 🔴 UNA BARRA, NO UNA PANTALLA PREVIA. Patricio: «los botones yo te decía de
   hacerlo en base a la barra y ponerlo debajo del ona pulse, en vez de que te
   lleve a una pantalla previa». Lo había entendido como navegación y era peor:
   un toque de más para ver lo mismo. Aquí se cambia en el sitio.
   Y lo que había antes tampoco valía: las slides de ocupación y las fichas de
   presupuesto apiladas una detrás de otra — «mezclo las slide», dijo. */
const SEGMENTOS = [
  { id: 'occ', t: 'Ocupación' },
  { id: 'prod', t: 'Producción' },
  { id: 'out', t: 'Outlets' },
] as const;
type Seg = typeof SEGMENTOS[number]['id'];

export default function HotelesScreen() {
  const [seg, setSeg] = useState<Seg>('occ');
  const [refrescando, setRefrescando] = useState(false);
  const [tiron, setTiron] = useState(valorActual());
  useEffect(() => suscribir(setTiron), []);
  const insets = useSafeAreaInsets();
  const { puede } = useRole();
  const verProd = puede('PRODUCCION');
  const verOcc = puede('OCUPACION');
  const [hoteles, setHoteles] = useState<HotelRow[]>([]);
  const [source, setSource] = useState<Source | undefined>(undefined);
  const [sel, setSel] = useState<HotelRow | null>(null);
  const [roleSheet, setRoleSheet] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.hoteles().then((r) => { setHoteles(r.data); setSource(r.source); }); }, [tiron]);

  return (
    <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: insets.top }}>
      <Header onAvatar={() => setRoleSheet(true)} source={source} />
            {/* Tirar hacia abajo para refrescar. Faltaba lo obvio: hasta hoy la
          única forma de traer datos nuevos era cerrar la app y abrirla otra
          vez. Un tirón recarga TODAS las tarjetas a la vez, no solo esta
          pantalla — el contador de lib/refresco.ts lo escuchan todas. */}
      <ScrollView
        refreshControl={<RefreshControl refreshing={refrescando} onRefresh={() => {
          setRefrescando(true); refrescarTodo();
          setTimeout(() => setRefrescando(false), 900);
        }} tintColor={C.aqua} colors={[C.aqua]} progressBackgroundColor={C.bg2} />} contentContainerStyle={{ paddingHorizontal: 18, paddingBottom: 28 }} showsVerticalScrollIndicator={false}>
        {/* El título lo pone la propia tarjeta, y así dice en qué vista estás.
            Repetirlo aquí era decir «Hoteles» encima de «Ocupación hoy». */}

        {/* UNA tarjeta, cuatro vistas que se pasan con el dedo: ocupación de
            hoy, de los 7 próximos, de los 30 y producción del mes. Antes había
            un bloque de ocupación arriba y el ranking debajo — dos cosas
            separadas contando lo mismo. Se lee igual en las cuatro, así que
            pasar de una a otra no obliga a reaprender nada. */}
        {/* 🔴 UN SEGMENTO ENSEÑA UNO, NO SE APILAN. Aquí faltaba la condición y
            por eso al pulsar «Producción» se cargaba DEBAJO de la ocupación en
            vez de sustituirla — «lo carga debajo en vez de hacer desaparecer la
            ocupación», dijo Patricio. Mi parche anterior no llegó a aplicarse y
            el error pasó las comprobaciones de tipos, que no ven esto. */}
        {/* 🔴 MIENTRAS LLEGAN LOS HOTELES, EL LATIDO. Antes esta pantalla se
            quedaba en BLANCO dos o tres segundos —solo el pie de página arriba,
            como en la captura de Patricio del 26-08— y una pantalla vacía no se
            distingue de una pantalla rota. */}
        {hoteles.length ? (
          <RankingCards
            hoteles={hoteles} verProd={verProd} verOcc={verOcc}
            onHotel={setSel}
          />
        ) : (
          <Cargando texto="Leyendo la ocupación y la producción de los hoteles…" alto={260} />
        )}

        <Text style={s.foot}>
          Toca un hotel para su ficha · desliza la tarjeta para cambiar de vista
          {hoteles[0]?.fecha ? ` · «ayer» = ${hoteles[0].fecha.slice(8, 10)}/${hoteles[0].fecha.slice(5, 7)}` : ''}
        </Text>
      </ScrollView>

      <BottomSheet visible={!!sel} onClose={() => setSel(null)}>
        {sel ? (() => {
          const rank = hoteles.findIndex((h) => (h.id ?? h.n) === (sel.id ?? sel.n)) + 1;
          const totalMes = hoteles.reduce((a, h) => a + h.mes, 0);
          const share = totalMes > 0 ? (sel.mes / totalMes) * 100 : null;
          const occ30 = (sel.occ30 ?? []).filter((p): p is number => p != null);
          const media30 = occ30.length ? occ30.reduce((a, b) => a + b, 0) / occ30.length : null;
          return (
            <ScrollView showsVerticalScrollIndicator={false} bounces={false} overScrollMode="never" contentContainerStyle={{ paddingBottom: 8 }}>
              <Text style={s.sheetTitle}>{sel.n}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <PmsBadge pms={sel.pms} />
                <Text style={s.sub2}>
                  {sel.d == null
                    ? `sin el ${ddmm(sel.fecha)} · último ${ddmm(sel.dFecha)}`
                    : `ayer${sel.fecha ? ` (${ddmm(sel.fecha)})` : ''} ${fmtE(sel.d)}`}
                </Text>
                <Spark vals={sel.spark} width={86} height={20} />
              </View>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                <Chip>{`nº ${rank} del mes`}</Chip>
                {share != null ? <Chip>{`${fmtDec(share)}% del grupo`}</Chip> : null}
                {sel.ob ? <Chip kind="bad">{`⚠ ${sel.ob} día(s) >100%`}</Chip> : null}
              </View>
              <View style={{ gap: 10 }}>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <KpiTile value={`${fmt(Math.round(sel.mes / 1e3))} k€`} label="Producción del mes"
                    delta={sel.bud != null ? `${sel.bud >= 100 ? '▲' : '▼'} ${fmtDec(sel.bud - 100)}% vs budget` : 'vs budget: próximamente'}
                    deltaKind={sel.bud != null ? (sel.bud >= 100 ? 'good' : 'bad') : 'flat'} />
                  <KpiTile value={sel.occ != null ? pct1(sel.occ) : '—'} label="Ocupación hoy"
                    delta={media30 != null ? `media 30 días: ${fmtDec(media30)}%` : 'rejilla 4 PMS'} deltaKind="flat" />
                </View>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <KpiTile value={sel.adr != null ? `${fmtDec(sel.adr)} €` : '—'} label="ADR del mes"
                    delta={sel.rn ? `${fmt(sel.rn)} noches` : 'próximamente'} deltaKind="flat" />
                  {/* 🔴 Nunca un 0 donde lo que hay es un dato que falta: se
                      enseña el último día que SÍ está y se dice que el otro no
                      ha llegado. Todos los hoteles cierran el mismo día. */}
                  <KpiTile
                    value={sel.d != null ? fmtE(sel.d) : (sel.dUlt != null ? fmtE(sel.dUlt) : '—')}
                    label={sel.d != null ? 'Producción ayer' : `Producción del ${ddmm(sel.dFecha)}`}
                    delta={sel.d != null ? 'espejo diario' : `falta el ${ddmm(sel.fecha)} en el espejo`}
                    deltaKind={sel.d != null ? 'flat' : 'bad'} />
                </View>
              </View>
              {sel.occ30?.length ? (
                <View style={{ marginTop: 14 }}>
                  <Text style={s.heatLbl}>OCUPACIÓN · PRÓXIMOS 30 DÍAS</Text>
                  <View style={s.heatRow}>
                    {sel.occ30.map((p, i) => (
                      <View key={i} style={[s.heatCell, { backgroundColor: p != null ? heatColor(p) : 'rgba(140,200,215,0.08)' }]} />
                    ))}
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                    <Text style={s.heatCap}>hoy</Text>
                    <Text style={s.heatCap}>+30 días · rojo = overbooking</Text>
                  </View>
                </View>
              ) : null}
              {(sel.pms === 'avalon' || sel.pms === 'navision') ? (
                <Text style={[s.foot2, { color: C.warn }]}>
                  ⚠ {sel.pms === 'avalon' ? 'Avalon' : 'Navision'} solo publica reservas: la producción es la de
                  las reservas vigentes — sin folio (cuenta huésped) ni cierre contable (por eso no compite por la Tortugona).
                </Text>
              ) : null}
              {/* 🔴 LOS PUNTOS DE VENTA, AL FINAL Y A PROPÓSITO. Patricio pidió
                  «desplegar para arriba con el dedo» y esta hoja ya desliza, así
                  que el bloque va abajo: se descubre con el mismo gesto, sin otro
                  toque ni otra pantalla. Y va DESPUÉS del pie de los importes
                  netos porque lo de arriba es la ficha que ya conocía; esto es lo
                  nuevo, y encontrarlo deslizando es parte de que se entienda que
                  hay más. */}
              <PuntosVenta hotel={sel} />
              <Text style={s.foot2}>Importes netos sin IVA (Navision/Avalon: neto si el hotel tiene su IVA en configuración; si no, con IVA y se declara).</Text>
            </ScrollView>
          );
        })() : null}
      </BottomSheet>

      <RoleSheet visible={roleSheet} onClose={() => setRoleSheet(false)} />
    </View>
  );
}

const s = StyleSheet.create({
  seg: {
    flex: 1, alignItems: 'center', paddingVertical: 9, borderRadius: 999,
    borderWidth: 1, borderColor: C.line, backgroundColor: C.bg2,
  },
  segOn: { backgroundColor: C.aqua, borderColor: C.aqua },
  segTxt: { fontFamily: F.uiMed, fontSize: 12.5, color: C.muted },
  segTxtOn: { color: '#04252A', fontFamily: F.uiBold },
  title: { fontFamily: F.displayMed, fontSize: 22, color: C.ink, marginTop: 6 },
  sub: { fontFamily: F.ui, fontSize: 12.5, color: C.muted, marginTop: 2, marginBottom: 12 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: C.line },
  rank: { width: 26, textAlign: 'center', fontFamily: F.ui, fontSize: 13, color: C.faint },
  name: { fontFamily: F.uiMed, fontSize: 13, color: C.ink },
  occCol: { width: 40, alignItems: 'flex-end' },
  occV: { fontFamily: F.uiBold, fontSize: 12.5, color: C.ink, fontVariant: ['tabular-nums'] },
  occL: { fontFamily: F.ui, fontSize: 8.5, color: C.faint, marginTop: 1 },
  mes: { fontFamily: F.uiBold, fontSize: 13, color: C.ink, fontVariant: ['tabular-nums'] },
  delta: { fontFamily: F.uiSemi, fontSize: 10, marginTop: 2 },
  foot: { fontFamily: F.ui, fontSize: 10, color: C.faint, textAlign: 'center', paddingVertical: 16 },
  veil: { flex: 1, backgroundColor: 'rgba(2,10,16,0.6)' },
  sheet: { backgroundColor: C.bg2, borderTopLeftRadius: R.sheet, borderTopRightRadius: R.sheet, paddingHorizontal: 20, paddingTop: 14 },
  grab: { width: 42, height: 5, borderRadius: 4, backgroundColor: 'rgba(140,169,184,0.35)', alignSelf: 'center', marginBottom: 16 },
  sheetTitle: { fontFamily: F.displayMed, fontSize: 20, color: C.ink, marginBottom: 8 },
  sub2: { fontFamily: F.ui, fontSize: 12, color: C.muted },
  foot2: { fontFamily: F.ui, fontSize: 10.5, color: C.faint, marginTop: 14, lineHeight: 15 },
  heatLbl: { fontFamily: F.uiSemi, fontSize: 10, letterSpacing: 1.3, color: C.muted, marginBottom: 6 },
  heatRow: { flexDirection: 'row', gap: 2 },
  heatCell: { flex: 1, height: 16, borderRadius: 3 },
  heatCap: { fontFamily: F.ui, fontSize: 9, color: C.faint },
});
